'use client'

import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { Search, Info, X, ChevronDown } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { Group } from '@/features/stm/lib/types'
import { useSession } from '@/features/auth/context/session.context'
import { authFetchJson } from '@/shared/lib/auth-fetch'

const NOTES_HEADING = 'Type @ to tag a volunteer'
const NOTES_INFO =
  'Use this field to add dated notes. Press Enter to start a new line and a timestamp with your name will be added automatically. Typing @ tags a volunteer for a notification.'

export type TaggedVolunteer = { id: number; fullname: string }

type GroupNotesCardProps = {
  group: Group
  notes: string
  onNotesChange: (value: string) => void
  taggedVolunteers?: TaggedVolunteer[]
  onTaggedChange?: (volunteers: TaggedVolunteer[]) => void
  /** When true (Group Leader view), main notes are read-only and a second "Add New Note" box is shown with @ tagging */
  readOnlyForGroupLeader?: boolean
  groupLeaderNewNote?: string
  onGroupLeaderNewNoteChange?: (value: string) => void
  /** When true, notes are fully read-only (no add/tag/filter edits) */
  readOnly?: boolean
}

/** Same format as Project General Notes: MM/DD/YY User Name: */
function formatDateStamp(fullname: string | null | undefined): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const year = String(now.getFullYear()).slice(-2)
  const userName = fullname || 'Unknown User'
  return `${month}/${day}/${year} ${userName}:`
}

export function GroupNotesCard({
  group,
  notes,
  onNotesChange,
  taggedVolunteers = [],
  onTaggedChange,
  readOnlyForGroupLeader = false,
  groupLeaderNewNote = '',
  onGroupLeaderNewNoteChange,
  readOnly = false,
}: GroupNotesCardProps) {
  const { profile } = useSession()
  const [notesExpanded, setNotesExpanded] = useState(false)
  const [pgnFilterKeyword, setPgnFilterKeyword] = useState('')
  const [filterDialogOpen, setFilterDialogOpen] = useState(false)
  const [pgnInfoPopoverOpen, setPgnInfoPopoverOpen] = useState(false)
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionStart, setMentionStart] = useState(0)
  const [mentionEnd, setMentionEnd] = useState(0)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [mentionDropdownRect, setMentionDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const newNoteTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const pendingSelectionRef = useRef<number | null>(null)
  const mentionListRef = useRef<HTMLUListElement | null>(null)

  const { data: teamLeadersData } = useQuery({
    queryKey: ['volunteers-team-leaders'],
    queryFn: async () => {
      const res = await authFetchJson<any[]>('/user/by-role?roleId=1,2,3,4')
      return res.success && res.data ? res.data : []
    },
  })

  const volunteerOptions = useMemo(() => {
    if (!Array.isArray(teamLeadersData)) return []
    const taggedIds = new Set(taggedVolunteers.map((v) => v.id))
    return teamLeadersData
      .filter((u: { id?: number; fullname?: string }) => u.id != null && !taggedIds.has(u.id))
      .map((u: { id: number; fullname?: string }) => ({ id: u.id, fullname: u.fullname || 'Unknown' }))
  }, [teamLeadersData, taggedVolunteers])

  const filteredMentionOptions = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase()
    if (!q) return volunteerOptions.slice(0, 10)
    return volunteerOptions
      .filter((v) => v.fullname.toLowerCase().includes(q))
      .slice(0, 10)
  }, [volunteerOptions, mentionQuery])

  const addTagged = useCallback(
    (v: TaggedVolunteer) => {
      onTaggedChange?.([...taggedVolunteers, v])
      setMentionQuery('')
      setMentionOpen(false)
    },
    [taggedVolunteers, onTaggedChange]
  )

  const insertMentionAndAddTagged = useCallback(
    (v: TaggedVolunteer) => {
      const insert = `@${v.fullname} `
      const newNotes =
        notes.slice(0, mentionStart) + insert + notes.slice(mentionEnd)
      onNotesChange(newNotes)
      addTagged(v)
      const newCursor = mentionStart + insert.length
      pendingSelectionRef.current = newCursor
    },
    [notes, mentionStart, mentionEnd, onNotesChange, addTagged]
  )

  const insertMentionAndAddTaggedForNewNote = useCallback(
    (v: TaggedVolunteer) => {
      const insert = `@${v.fullname} `
      const current = groupLeaderNewNote ?? ''
      const newValue = current.slice(0, mentionStart) + insert + current.slice(mentionEnd)
      onGroupLeaderNewNoteChange?.(newValue)
      addTagged(v)
      const newCursor = mentionStart + insert.length
      pendingSelectionRef.current = newCursor
    },
    [groupLeaderNewNote, mentionStart, mentionEnd, onGroupLeaderNewNoteChange, addTagged]
  )

  const removeTagged = useCallback(
    (id: number) => {
      onTaggedChange?.(taggedVolunteers.filter((t) => t.id !== id))
    },
    [taggedVolunteers, onTaggedChange]
  )

  // After inserting a stamp or mention, move cursor to the right position and scroll into view
  useEffect(() => {
    const position = pendingSelectionRef.current
    if (position === null) return
    pendingSelectionRef.current = null
    const raf = requestAnimationFrame(() => {
      if (readOnlyForGroupLeader) {
        const el = newNoteTextareaRef.current
        const maxPos = (groupLeaderNewNote ?? '').length
        if (el && position >= 0 && position <= maxPos) {
          el.focus()
          el.setSelectionRange(position, position)
          el.scrollTop = el.scrollHeight - el.clientHeight
        }
      } else {
        const el = textareaRef.current
        if (el && position >= 0 && position <= notes.length) {
          el.focus()
          el.setSelectionRange(position, position)
          el.scrollTop = el.scrollHeight - el.clientHeight
        }
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [notes, groupLeaderNewNote, readOnlyForGroupLeader])

  // Scroll selected mention option into view
  useEffect(() => {
    if (!mentionOpen || !mentionListRef.current) return
    const selected = mentionListRef.current.children[mentionSelectedIndex] as HTMLElement | undefined
    selected?.scrollIntoView({ block: 'nearest' })
  }, [mentionOpen, mentionSelectedIndex])

  // Position mention dropdown via portal so it isn't clipped by accordion overflow (use fixed for viewport-relative)
  const updateMentionRect = useCallback(() => {
    const el = readOnlyForGroupLeader ? newNoteTextareaRef.current : textareaRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setMentionDropdownRect({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    })
  }, [readOnlyForGroupLeader])

  useEffect(() => {
    if (!mentionOpen || !(readOnlyForGroupLeader ? newNoteTextareaRef.current : textareaRef.current)) {
      setMentionDropdownRect(null)
      return
    }
    updateMentionRect()
    window.addEventListener('scroll', updateMentionRect, true)
    window.addEventListener('resize', updateMentionRect)
    return () => {
      window.removeEventListener('scroll', updateMentionRect, true)
      window.removeEventListener('resize', updateMentionRect)
    }
  }, [mentionOpen, notes, groupLeaderNewNote, readOnlyForGroupLeader, updateMentionRect])

  const notesLines = useMemo(() => {
    const text = notes ?? ''
    return text.split('\n').filter((line) => line.trim().length > 0)
  }, [notes])

  const filteredLines = useMemo(() => {
    if (!pgnFilterKeyword.trim()) return notesLines
    const kw = pgnFilterKeyword.trim().toLowerCase()
    return notesLines.filter((line) => line.toLowerCase().includes(kw))
  }, [notesLines, pgnFilterKeyword])

  const isFilterActive = pgnFilterKeyword.trim().length > 0

  const handleNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      const start = e.target.selectionStart ?? value.length
      onNotesChange(value)
      const beforeCursor = value.slice(0, start)
      const lastAt = beforeCursor.lastIndexOf('@')
      const hasSpaceAfterAt =
        lastAt >= 0 && beforeCursor.slice(lastAt + 1).includes(' ')
      if (lastAt >= 0 && !hasSpaceAfterAt) {
        setMentionStart(lastAt)
        setMentionEnd(start)
        setMentionQuery(beforeCursor.slice(lastAt + 1))
        setMentionOpen(true)
        setMentionSelectedIndex(0)
      } else {
        setMentionOpen(false)
      }
    },
    [onNotesChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionOpen && filteredMentionOptions.length > 0) {
        if (e.key === 'Escape') {
          e.preventDefault()
          setMentionOpen(false)
          return
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          const v = filteredMentionOptions[mentionSelectedIndex]
          if (v) insertMentionAndAddTagged(v)
          return
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setMentionSelectedIndex((i) =>
            i < filteredMentionOptions.length - 1 ? i + 1 : 0
          )
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setMentionSelectedIndex((i) =>
            i > 0 ? i - 1 : filteredMentionOptions.length - 1
          )
          return
        }
      }
      if (e.key !== 'Enter') return
      const el = textareaRef.current
      const start = el?.selectionStart ?? notes.length
      const end = el?.selectionEnd ?? start
      const hasContent = notes.trim().length > 0
      if (!el || !hasContent) return
      const dateStamp = formatDateStamp(profile?.fullname)
      const before = notes.slice(0, start)
      const after = notes.slice(end)
      const lastNewline = before.lastIndexOf('\n')
      const currentLine = lastNewline === -1 ? before : before.slice(lastNewline + 1)
      const isEmptyStampedLine = currentLine === dateStamp || currentLine === dateStamp + ' '
      if (isEmptyStampedLine) {
        requestAnimationFrame(() => {
          const ta = textareaRef.current
          if (ta) ta.scrollTop = ta.scrollHeight - ta.clientHeight
        })
        return
      }
      e.preventDefault()
      const newValue = `${before}\n${dateStamp} ${after}`
      const newCursorPos = before.length + 1 + dateStamp.length + 1
      pendingSelectionRef.current = newCursorPos
      onNotesChange(newValue)
    },
    [
      notes,
      profile?.fullname,
      onNotesChange,
      mentionOpen,
      filteredMentionOptions,
      mentionSelectedIndex,
      insertMentionAndAddTagged,
    ]
  )

  const handleFocus = useCallback(() => {
    const current = notes.trim()
    if (current === '') {
      const stamp = formatDateStamp(profile?.fullname) + ' '
      onNotesChange(stamp)
      pendingSelectionRef.current = stamp.length
    }
  }, [notes, profile?.fullname, onNotesChange])

  const handleNotesBlur = useCallback(() => {
    setMentionOpen(false)
  }, [])

  const newNoteValue = groupLeaderNewNote ?? ''
  const handleNewNoteChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      const start = e.target.selectionStart ?? value.length
      onGroupLeaderNewNoteChange?.(value)
      const beforeCursor = value.slice(0, start)
      const lastAt = beforeCursor.lastIndexOf('@')
      const hasSpaceAfterAt = lastAt >= 0 && beforeCursor.slice(lastAt + 1).includes(' ')
      if (lastAt >= 0 && !hasSpaceAfterAt) {
        setMentionStart(lastAt)
        setMentionEnd(start)
        setMentionQuery(beforeCursor.slice(lastAt + 1))
        setMentionOpen(true)
        setMentionSelectedIndex(0)
      } else {
        setMentionOpen(false)
      }
    },
    [onGroupLeaderNewNoteChange]
  )

  const handleNewNoteKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionOpen && filteredMentionOptions.length > 0) {
        if (e.key === 'Escape') {
          e.preventDefault()
          setMentionOpen(false)
          return
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          const v = filteredMentionOptions[mentionSelectedIndex]
          if (v) insertMentionAndAddTaggedForNewNote(v)
          return
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setMentionSelectedIndex((i) => (i < filteredMentionOptions.length - 1 ? i + 1 : 0))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setMentionSelectedIndex((i) => (i > 0 ? i - 1 : filteredMentionOptions.length - 1))
          return
        }
      }
      if (e.key !== 'Enter') return
      const el = newNoteTextareaRef.current
      const start = el?.selectionStart ?? newNoteValue.length
      const end = el?.selectionEnd ?? start
      const hasContent = newNoteValue.trim().length > 0
      if (!el || !hasContent) return
      const dateStamp = formatDateStamp(profile?.fullname)
      const before = newNoteValue.slice(0, start)
      const after = newNoteValue.slice(end)
      const lastNewline = before.lastIndexOf('\n')
      const currentLine = lastNewline === -1 ? before : before.slice(lastNewline + 1)
      const isEmptyStampedLine = currentLine === dateStamp || currentLine === dateStamp + ' '
      if (isEmptyStampedLine) {
        requestAnimationFrame(() => {
          const ta = newNoteTextareaRef.current
          if (ta) ta.scrollTop = ta.scrollHeight - ta.clientHeight
        })
        return
      }
      e.preventDefault()
      const newValue = `${before}\n${dateStamp} ${after}`
      const newCursorPos = before.length + 1 + dateStamp.length + 1
      pendingSelectionRef.current = newCursorPos
      onGroupLeaderNewNoteChange?.(newValue)
    },
    [
      newNoteValue,
      profile?.fullname,
      onGroupLeaderNewNoteChange,
      mentionOpen,
      filteredMentionOptions,
      mentionSelectedIndex,
      insertMentionAndAddTaggedForNewNote,
    ]
  )

  const handleNewNoteFocus = useCallback(() => {
    const current = (groupLeaderNewNote ?? '').trim()
    if (current === '') {
      const stamp = formatDateStamp(profile?.fullname) + ' '
      onGroupLeaderNewNoteChange?.(stamp)
      pendingSelectionRef.current = stamp.length
    }
  }, [groupLeaderNewNote, profile?.fullname, onGroupLeaderNewNoteChange])

  const actionButtons = (
    <>
      {onTaggedChange != null && (
        <span className="text-xs text-muted-foreground shrink-0">
          Type @ to tag a volunteer
        </span>
      )}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="default"
            size="sm"
            className="shrink-0 bg-primary hover:bg-primary/90 text-white font-bold h-6 w-8 rounded-full p-0"
            aria-label="Filter notes by keyword"
          >
            <Search className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] max-w-lg" aria-describedby={undefined}>
          <DialogTitle>Filter notes by keyword</DialogTitle>
          <div className="space-y-4 overflow-y-auto max-h-[calc(90vh-120px)] pr-2">
            <div className="space-y-2">
              <Label htmlFor="group-notes-filter-keyword" className="text-sm">
                Keyword
              </Label>
              <Input
                id="group-notes-filter-keyword"
                type="text"
                placeholder="Type to filter notes..."
                value={pgnFilterKeyword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPgnFilterKeyword((e.target as HTMLInputElement).value)
                }
                autoFocus
              />
            </div>
            {isFilterActive && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {filteredLines.length === 0 ? (
                    <>No notes match &quot;{pgnFilterKeyword.trim()}&quot;</>
                  ) : (
                    <>
                      Showing {filteredLines.length} of {notesLines.length} note
                      {notesLines.length !== 1 ? 's' : ''} matching &quot;
                      {pgnFilterKeyword.trim()}&quot;
                    </>
                  )}
                </p>
                {filteredLines.length > 0 && (
                  <div className="rounded-md border border-input bg-muted/30 p-3 max-h-64 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-sans">
                      {filteredLines.join('\n')}
                    </pre>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 pt-2">
              {isFilterActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPgnFilterKeyword('')}
                >
                  Clear filter
                </Button>
              )}
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => setFilterDialogOpen(false)}
                className="ml-auto"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )

  const infoIcon = (
    <Popover open={pgnInfoPopoverOpen} onOpenChange={setPgnInfoPopoverOpen}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Notes info"
              >
                <Info className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[260px] text-xs">
            <p className="font-medium mb-1">{NOTES_HEADING}</p>
            <p>{NOTES_INFO}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent side="bottom" className="max-w-[280px] text-sm">
        <p className="font-medium mb-1">{NOTES_HEADING}</p>
        <p>{NOTES_INFO}</p>
      </PopoverContent>
    </Popover>
  )

  if (readOnly) {
    return (
      <div className="space-y-4 pt-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <span className="text-sm font-medium">Group notes</span>
            {infoIcon}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setNotesExpanded(!notesExpanded)}
            className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={notesExpanded ? 'Collapse notes' : 'Expand notes to read more'}
          >
            <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', notesExpanded && 'rotate-180')} />
          </Button>
        </div>
        <div
          className={cn(
            'overflow-y-auto rounded-md border border-input bg-muted/50 p-3 transition-[max-height] duration-200',
            notesExpanded ? 'max-h-[320px]' : 'max-h-[168px]'
          )}
        >
          {notes?.trim() ? (
            <pre className="whitespace-pre-wrap text-sm font-sans text-muted-foreground">{notes}</pre>
          ) : (
            <p className="text-sm text-muted-foreground italic">No notes yet</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 pt-2">
      {readOnlyForGroupLeader ? (
        <>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <span className="text-sm font-medium">Group notes</span>
              {infoIcon}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {actionButtons}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setNotesExpanded(!notesExpanded)}
                className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                aria-label={notesExpanded ? 'Collapse notes' : 'Expand notes to read more'}
              >
                <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', notesExpanded && 'rotate-180')} />
              </Button>
            </div>
          </div>
          <div
            className={cn(
              'overflow-y-auto rounded-md border border-input bg-muted/50 p-3 transition-[max-height] duration-200',
              notesExpanded ? 'max-h-[320px]' : 'max-h-[168px]'
            )}
          >
            {notes?.trim() ? (
              <pre className="whitespace-pre-wrap text-sm font-sans text-muted-foreground">{notes}</pre>
            ) : (
              <p className="text-sm text-muted-foreground italic">No notes yet</p>
            )}
          </div>
          {taggedVolunteers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-xs text-muted-foreground">Notify on save:</span>
              {taggedVolunteers.map((v) => (
                <span
                  key={v.id}
                  className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary px-2 py-0.5 text-sm"
                >
                  {v.fullname}
                  <button
                    type="button"
                    aria-label={`Remove ${v.fullname} from tag list`}
                    className="hover:bg-primary/20 rounded p-0.5"
                    onClick={() => removeTagged(v.id)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Add New Note (Group Leader)</Label>
            <div className="relative">
              <div className="rounded-md border border-input bg-background">
                <Textarea
                  ref={newNoteTextareaRef}
                  value={newNoteValue}
                  onChange={handleNewNoteChange}
                  onKeyDown={handleNewNoteKeyDown}
                  onFocus={handleNewNoteFocus}
                  onBlur={handleNotesBlur}
                  placeholder="Add dated notes... (type @ to tag a volunteer)"
                  className="min-h-0 border-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0 rounded-md"
                  rows={5}
                />
              </div>
              {mentionOpen &&
                onTaggedChange != null &&
                mentionDropdownRect &&
                typeof document !== 'undefined' &&
                createPortal(
                  <div
                    className="fixed z-[100] rounded-md border border-input bg-popover text-popover-foreground shadow-md overflow-hidden"
                    role="listbox"
                    style={{
                      top: mentionDropdownRect.top,
                      left: mentionDropdownRect.left,
                      width: mentionDropdownRect.width,
                      minWidth: 200,
                    }}
                  >
                    <div className="p-2 max-h-52 overflow-y-auto">
                      {filteredMentionOptions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2 px-2">
                          {volunteerOptions.length === 0
                            ? taggedVolunteers.length > 0
                              ? 'All volunteers are already tagged.'
                              : 'No volunteers available.'
                            : `No match for "${mentionQuery.trim() || '@'}"`}
                        </p>
                      ) : (
                        <ul ref={mentionListRef} className="space-y-0.5">
                          {filteredMentionOptions.map((v, i) => (
                            <li key={v.id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={i === mentionSelectedIndex}
                                className={`w-full text-left text-sm py-2 px-3 rounded ${
                                  i === mentionSelectedIndex ? 'bg-muted' : 'hover:bg-muted/70'
                                }`}
                                onMouseDown={(e) => {
                                  e.preventDefault()
                                  insertMentionAndAddTaggedForNewNote(v)
                                }}
                              >
                                {v.fullname}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          </div>
        </>
      ) : (
        <>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <span className="text-sm font-medium">Group notes</span>
          {infoIcon}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {actionButtons}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setNotesExpanded(!notesExpanded)}
            className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={notesExpanded ? 'Collapse notes' : 'Expand notes to read more'}
          >
            <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', notesExpanded && 'rotate-180')} />
          </Button>
        </div>
      </div>
      {taggedVolunteers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-muted-foreground">Notify on save:</span>
          {taggedVolunteers.map((v) => (
            <span
              key={v.id}
              className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary px-2 py-0.5 text-sm"
            >
              {v.fullname}
              <button
                type="button"
                aria-label={`Remove ${v.fullname} from tag list`}
                className="hover:bg-primary/20 rounded p-0.5"
                onClick={() => removeTagged(v.id)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <div
          className={cn(
            'overflow-y-auto rounded-md border border-input bg-background transition-[max-height] duration-200',
            notesExpanded ? 'max-h-[320px]' : 'max-h-[168px]'
          )}
        >
          <Textarea
            ref={textareaRef}
            value={notes}
            onChange={handleNotesChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleNotesBlur}
            placeholder="Add dated notes... (type @ to tag a volunteer)"
            className="min-h-0 border-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0 rounded-md"
            rows={7}
          />
        </div>
        {mentionOpen &&
          onTaggedChange != null &&
          mentionDropdownRect &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              className="fixed z-[100] rounded-md border border-input bg-popover text-popover-foreground shadow-md overflow-hidden"
              role="listbox"
              style={{
                top: mentionDropdownRect.top,
                left: mentionDropdownRect.left,
                width: mentionDropdownRect.width,
                minWidth: 200,
              }}
            >
              <div className="p-2 max-h-52 overflow-y-auto">
                {filteredMentionOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2 px-2">
                    {volunteerOptions.length === 0
                      ? taggedVolunteers.length > 0
                        ? 'All volunteers are already tagged.'
                        : 'No volunteers available.'
                      : `No match for "${mentionQuery.trim() || '@'}"`}
                  </p>
                ) : (
                  <ul ref={mentionListRef} className="space-y-0.5">
                    {filteredMentionOptions.map((v, i) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={i === mentionSelectedIndex}
                          className={`w-full text-left text-sm py-2 px-3 rounded ${
                            i === mentionSelectedIndex ? 'bg-muted' : 'hover:bg-muted/70'
                          }`}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            insertMentionAndAddTagged(v)
                          }}
                        >
                          {v.fullname}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
        </>
      )}
    </div>
  )
}
